import {
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Skeleton,
} from "@/components/ui";
import { LanguageNames } from "@/constants/Languages";
import { QueryKeys } from "@/constants/Query";
import { TRACKERS } from "@/constants/Trackers";
import { useVisitDiagnoses } from "@/features/Charting/network/useVisitDiagnoses";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { trackEvent } from "@/lib/tracking/tracking";
import { constants, MedicationRequest, getDisplay } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";
import { useEffect, useMemo } from "react";
import { DURATION_UNITS } from "../components/FollowUp/schemas";
import { useDisposition } from "../components/network/useDisposition";
import { useFollowUp } from "../components/network/useFollowUp";
import { useLabOrder } from "../components/network/useLabOrder";
import { useRecommendations } from "../components/network/useRecommendations";
import { useTranslateCarePlan } from "../components/network/useTranslateCarePlan";
import { usePrescriptions } from "../network/usePrescriptions";
import { useSendCareplan } from "./useSendCareplan";

interface CarePlanPreviewModal {
  open: boolean;
  onOpenChange(o: boolean): void;
  carePlanId: string;
  encounterId: string;
  patientId: string;
  condition?: fhirTypes.Condition;
}

interface VisitDiagnosisListItem {
  key: string;
  name: string;
}

export function CarePlanPreviewModal({
  open,
  onOpenChange,
  carePlanId,
  encounterId,
  patientId,
  condition,
}: CarePlanPreviewModal) {
  const queryClient = useQueryClient();
  const { data: patient } = useGetPatient(patientId);
  const preferredLanguage =
    patient?.preferredLanguage || constants.LanguageCode.ENGLISH;

  const { data: visitDiagnosesData } = useVisitDiagnoses(
    encounterId,
    open && !condition,
  );
  const visitDiagnosisListItems =
    visitDiagnosesData?.visitDiagnoses.reduce<VisitDiagnosisListItem[]>(
      (currentDiagnosisListItems, diagnosis) => {
        const diagnosisName = diagnosis.displayName.trim();
        if (!diagnosisName) {
          return currentDiagnosisListItems;
        }

        const conditionId = diagnosis.id?.trim() || undefined;
        const snomedCode = diagnosis.snomedConcept?.code || undefined;
        const baseKey =
          conditionId ?? snomedCode ?? `diagnosis-${diagnosisName}`;
        const usedKeys = new Set(
          currentDiagnosisListItems.map(({ key }) => key),
        );
        let key = baseKey;
        let occurrence = 0;
        while (usedKeys.has(key)) {
          occurrence += 1;
          key = `${baseKey}-${occurrence}`;
        }

        currentDiagnosisListItems.push({ key, name: diagnosisName });

        return currentDiagnosisListItems;
      },
      [],
    ) ?? [];
  const conditionName = condition?.code ? getDisplay(condition.code) : null;
  const { mutateAsync: sendCareplanMutation, isPending: isSendingCareplan } =
    useSendCareplan({ carePlanId, patientId });

  const {
    data: recommendationsData,
    isFetching: isLoadingRecommendations,
    isError: isErrorRecommendations,
  } = useRecommendations({
    encounterId: encounterId,
    carePlanId,
    enabled: open,
  });

  const {
    data: dispositionData,
    isFetching: isLoadingDisposition,
    isError: isErrorDisposition,
  } = useDisposition({
    encounterId: encounterId,
    carePlanId,
    enabled: open,
  });

  const {
    data: followUpData,
    isFetching: isLoadingFollowUp,
    isError: isErrorFollowUp,
  } = useFollowUp({
    encounterId,
    carePlanId,
    enabled: open,
  });

  const {
    data: prescriptionData,
    isFetching: isLoadingPrescriptions,
    isError: isErrorPrescriptions,
  } = usePrescriptions({
    encounterId: encounterId,
    patientId,
    enabled: open,
  });

  const {
    data: labOrderData,
    isFetching: isLoadingLabOrder,
    isError: isErrorLabOrder,
  } = useLabOrder({
    encounterId: encounterId,
    patientId,
    enabled: open,
  });

  const {
    mutate: translateCareplanMutation,
    isPending: isTranslatingCareplan,
    isError: isErrorTranslatingCareplan,
  } = useTranslateCarePlan({
    patientId,
    encounterId: encounterId,
    carePlanId,
  });

  const isLoading =
    isLoadingRecommendations ||
    isLoadingDisposition ||
    isLoadingFollowUp ||
    isLoadingPrescriptions ||
    isLoadingLabOrder;

  const isError =
    isErrorRecommendations ||
    isErrorDisposition ||
    isErrorFollowUp ||
    isErrorPrescriptions ||
    isErrorLabOrder;

  const isFatalError = !isLoading && isError;

  const incompletePrescriptions = useMemo(() => {
    if (!prescriptionData?.rxs) return [];
    return prescriptionData.rxs
      .map((rx) => new MedicationRequest(rx.medicationRequest))
      .filter((medicationRequest) => !medicationRequest.isCompletedStatus);
  }, [prescriptionData]);

  const list = useMemo(() => {
    const medications =
      prescriptionData?.rxs.map((rx) => {
        const medicationRequest = new MedicationRequest(rx.medicationRequest);

        return `${medicationRequest.medicationCodeableConcept?.text} has been prescribed to you. [Directions: ${medicationRequest.sig}]`;
      }) || [];

    const testsOrdered =
      labOrderData?.labOrders.flatMap((labOrder) => {
        const tests = labOrder.tests.map((test) => {
          return `${test.testName} has been ordered.`;
        });

        return tests;
      }) || [];

    const recommendations =
      recommendationsData?.recommendationTasks.map(
        (task) => task.description,
      ) || [];

    const dispositions =
      dispositionData?.dispositionTasks.map((task) => task.description) || [];

    const hasFollowUp =
      followUpData?.followupDurationUnit && followUpData?.followupDurationValue;
    const followUpDurationUnit = DURATION_UNITS.find(
      ({ value }) => followUpData?.followupDurationUnit === value,
    )?.label.toLowerCase();
    const followUpText = hasFollowUp
      ? `We'll follow up with you in ${followUpData?.followupDurationValue} ${followUpDurationUnit}. Please contact us sooner if you have any questions or concerns.`
      : null;

    return [
      ...medications,
      ...testsOrdered,
      ...recommendations,
      ...dispositions,
      ...(followUpText ? [followUpText] : []),
    ];
  }, [
    recommendationsData,
    dispositionData,
    followUpData,
    prescriptionData,
    labOrderData,
  ]);

  const sendCareplan = async (carePlanId: string) => {
    if (preferredLanguage === constants.LanguageCode.ENGLISH) {
      trackEvent(TRACKERS.SEND_CARE_PLAN_PREVIEW_MODAL);
    } else {
      trackEvent(TRACKERS.SEND_IN_ENGLISH_CARE_PLAN_PREVIEW_MODAL);
    }
    onOpenChange(false);
    await sendCareplanMutation(carePlanId);
  };

  const translateAndSendCareplan = async () => {
    trackEvent(TRACKERS.SEND_AND_TRANSLATE_CARE_PLAN_PREVIEW_MODAL);
    translateCareplanMutation(
      {
        targetLanguage: preferredLanguage as constants.LanguageCode,
      },
      {
        onSuccess: async () => {
          onOpenChange(false);
          await sendCareplanMutation(carePlanId);
        },
      },
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Always invalidate on open or close for safety
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.PREVIEW({
        patientId,
        carePlanId,
      }),
    });
  }, [open, queryClient, carePlanId, patientId]);

  const cancelDialog = () => {
    trackEvent(TRACKERS.CANCEL_SEND_CARE_PLAN);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="w-full max-w-md"
          runOnDialogCloseIconClick={cancelDialog}
          onInteractOutside={cancelDialog}
        >
          <DialogHeader>
            <DialogTitle>Care Plan Preview</DialogTitle>
            <DialogDescription className="hidden">
              care plan preview modal
            </DialogDescription>
          </DialogHeader>
          {isFatalError && (
            <Callout
              className="w-full"
              variant="danger"
              title="Failed to load care plan preview!"
              description="Please refresh and try again."
            />
          )}
          {conditionName ? (
            <BodyText size="md">{conditionName}</BodyText>
          ) : visitDiagnosisListItems.length ? (
            <Callout variant="grayscale" title="Visit Diagnoses">
              <ul aria-label="Visit diagnoses" className="list-disc pl-4 mt-1">
                {visitDiagnosisListItems.map((diagnosis) => (
                  <li key={diagnosis.key}>
                    <BodyText size="sm">{diagnosis.name}</BodyText>
                  </li>
                ))}
              </ul>
            </Callout>
          ) : (
            <BodyText size="md">Unknown EOC</BodyText>
          )}
          {incompletePrescriptions.length > 0 && (
            <Callout
              className="w-full"
              variant="warning"
              title="Incomplete Prescriptions"
            >
              <BodyText size="sm" className="mb-2">
                The following {incompletePrescriptions.length} prescription
                {incompletePrescriptions.length > 1 ? "s" : ""}{" "}
                {incompletePrescriptions.length > 1 ? "have" : "has"} not been
                sent or called in to the pharmacy:
              </BodyText>
              <ul className="list-disc pl-4">
                {incompletePrescriptions.map((medicationRequest) => {
                  const medicationName =
                    medicationRequest.medicationName || "Unknown medication";
                  return (
                    <li key={medicationRequest.id || medicationName}>
                      <BodyText size="sm">{medicationName}</BodyText>
                    </li>
                  );
                })}
              </ul>
              <BodyText size="sm" className="mb-2">
                Please send or call in the prescriptions to the pharmacy in
                order to send the care plan.
              </BodyText>
            </Callout>
          )}

          {isLoading && (
            <div className="grid gap-1">
              {Array.from({ length: 8 }).map((_, i) => (
                //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
                <Skeleton className="h-8" key={i} />
              ))}
            </div>
          )}

          {!isLoading && !isError && (
            <div className="max-h-[70vh] overflow-y-auto">
              <ul className="list-disc pl-4">
                {list?.map((task) => {
                  return (
                    <li className="mb-2 leading-15" key={task}>
                      <BodyText size="sm" preserveLineBreaks>
                        {task}
                      </BodyText>
                    </li>
                  );
                })}
              </ul>
              {!list?.length && (
                <Callout
                  variant="warning"
                  title="No Care Plan Items"
                  description="Care plan cannot be sent without at least one order, instruction, or follow-up."
                />
              )}
            </div>
          )}

          {isErrorTranslatingCareplan && (
            <Callout
              className="w-full"
              variant="danger"
              title={`Translation to ${LanguageNames[preferredLanguage as keyof typeof LanguageNames]} Failed`}
              description="Try translating and sending again, or send in English if translation continues to fail."
            />
          )}

          {!isLoading && !isError && (
            <div className="flex flex-row gap-2 justify-self-end">
              {preferredLanguage !== constants.LanguageCode.ENGLISH && (
                <Button
                  className="w-fit ml-auto"
                  size="sm"
                  disabled={
                    isSendingCareplan || incompletePrescriptions.length > 0
                  }
                  isLoading={isTranslatingCareplan || isSendingCareplan}
                  onClick={() => translateAndSendCareplan()}
                >
                  Translate & Send
                </Button>
              )}
              <Button
                className="w-fit ml-auto"
                size="sm"
                disabled={
                  !list?.length ||
                  isTranslatingCareplan ||
                  incompletePrescriptions.length > 0
                }
                isLoading={isSendingCareplan}
                onClick={() => sendCareplan(carePlanId)}
              >
                {preferredLanguage === constants.LanguageCode.ENGLISH
                  ? "Send Careplan"
                  : "Send in English"}
              </Button>
            </div>
          )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
